import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import styles from './Products.module.css';

import TopBar from './components/TopBar';
import FiltersSidebar from './components/FiltersSidebar';
import ProductGrid from './components/ProductGrid';

import { applyAllFilters, defaultFilters } from './utils/filterUtils';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import backend from '../../../../api';

// Prefer backend proxy (recommended)
const USE_PROXY = true;

export default function Products() {
  const PRIMARY = '#ffce00';

  const theme = useMemo(
    () =>
      createTheme({
        palette: { primary: { main: PRIMARY } },
        components: {
          MuiFormControlLabel: { styleOverrides: { label: { fontSize: 'var(--mui-font-sm)' } } },
          MuiSwitch: {
            defaultProps: { size: 'small' },
            styleOverrides: {
              root: { padding: 4 },
              switchBase: {
                color: PRIMARY,
                '&.Mui-checked': { color: PRIMARY },
                '&.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY, opacity: 1 },
              },
              thumb: { width: 'var(--mui-thumb-size)', height: 'var(--mui-thumb-size)' },
              track: { backgroundColor: 'rgba(0,0,0,0.16)', opacity: 1 },
            },
          },
          MuiCheckbox: { defaultProps: { size: 'small' }, styleOverrides: { root: { color: PRIMARY, '&.Mui-checked': { color: PRIMARY } } } },
          MuiSlider: {
            defaultProps: { size: 'small' },
            styleOverrides: {
              root: { color: PRIMARY, height: 'var(--mui-slider-thickness)' },
              rail: { height: 'var(--mui-slider-thickness)', opacity: 1, background: 'rgba(0,0,0,0.16)' },
              track: { height: 'var(--mui-slider-thickness)' },
              thumb: { width: 'var(--mui-thumb-size)', height: 'var(--mui-thumb-size)' },
            },
          },
          MuiTextField: { defaultProps: { size: 'small' } },
          MuiSelect: {
            defaultProps: { size: 'small' },
            styleOverrides: {
              select: { fontSize: 'var(--mui-font-sm)', minHeight: 'var(--mui-ctl-height) !important', padding: '0 10px !important', display: 'flex', alignItems: 'center' },
              icon: { color: '#9ca3af' },
              iconOpen: { color: PRIMARY },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              input: { fontSize: 'var(--mui-font-sm)', height: 'var(--mui-ctl-height)', padding: '0 10px' },
              notchedOutline: { borderColor: '#e5e7eb' },
              root: {
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: { fontSize: 'var(--mui-font-sm)', color: '#6b7280' },
              shrink: { transform: 'translate(14px, -9px) scale(0.85)' },
            },
          },
          MuiMenu: { styleOverrides: { paper: { padding: 4 }, list: { paddingTop: 0, paddingBottom: 0 } } },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                fontSize: 'var(--mui-font-sm)',
                minHeight: 'var(--mui-ctl-height)',
                '&.Mui-selected': { backgroundColor: 'rgba(255,206,0,0.18)' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(255,206,0,0.22)' },
              },
            },
          },
        },
      }),
    []
  );

  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filters, setFilters] = useState(defaultFilters);

  const [isBootLoading, setIsBootLoading] = useState(true);
  const [dbProducts, setDbProducts] = useState([]);

  // External (BarcodeLookup) state
  const [extItems, setExtItems] = useState([]);      // accumulated items (mapped to card model)
  const extPageRef = useRef(1);
  const [extHasMore, setExtHasMore] = useState(true);
  const [extLoading, setExtLoading] = useState(false);

  // --- Helpers ---
  const isLightingLike = (txt) => {
    if (!txt) return false;
    const s = String(txt).toLowerCase();
    return (
      s.includes('light') || s.includes('lighting') || s.includes('flashlight') ||
      s.includes('torch') || s.includes('headlamp') || s.includes('led') ||
      s.includes('lamp') || s.includes('lantern') || s.includes('spotlight') ||
      s.includes('floodlight') || s.includes('bulb')
    );
  };

  // DB → card shape
  const mapDbToCard = (p) => ({
    id: p._id,
    brand: p.brand,
    brandShort: p.brandShort,
    discount: p.discount,
    name: p.productName,
    subtitle: p.description,
    category: p.category || p.usage || '',
    color: (p.color || '').toLowerCase(),
    specs: [
      p.usage && `Usage: ${p.usage}`,
      p.dimmable && `Dimmable: ${p.dimmable}`,
      p.temperature && `Temp: ${p.temperature}`,
      p.beamAngle && `Beam: ${p.beamAngle}`,
      p.wattage && `Watt: ${p.wattage}`
    ].filter(Boolean),
    image: p.image || p.images?.[0] || "/assets/images/placeholder.png",
    price: Number(p.price || 0),
    available: !!p.available,
    source: 'local',
    // attributes used by advanced filters (present only for local items)
    mountings: p.mountings || [],
    shape: p.shape || '',
    adjustable: !!p.adjustable,
    wallMounted: !!p.wallMounted,
    baseIncluded: !!p.baseIncluded,
    rgbw: !!p.rgbw,
    tunableWhite: !!p.tunableWhite,
    controls: p.controls || [],
    power: Number.isFinite(Number(p.power)) ? Number(p.power) : undefined,
    heads: Number.isFinite(Number(p.heads)) ? Number(p.heads) : undefined,
    lumens: Number.isFinite(Number(p.lumens)) ? Number(p.lumens) : undefined,
    efficiency: Number.isFinite(Number(p.efficiency)) ? Number(p.efficiency) : undefined,
    voltage: p.voltage || '',
    raw: p,
  });

  // BarcodeLookup → card shape
  const mapExtToCard = (it, idx = 0) => {
    const firstImage =
      (Array.isArray(it?.images) && it.images.length && it.images[0]) ||
      it?.image_url ||
      "/assets/images/placeholder.png";

    const rawStorePrice =
      (Array.isArray(it?.stores) && it.stores.length && it.stores.find(s => s?.price)?.price) || null;

    const parsedPrice = rawStorePrice
      ? Number(String(rawStorePrice).replace(/[^\d.]/g, ''))
      : Number(it?.list_price || 0);

    const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice : 0;

    const name = it?.title || it?.product_name || it?.model || 'Lighting Product';

    return {
      id: `ext:${it?.barcode_number || it?.mpn || it?.asin || it?.model || `row_${idx}`}`,
      brand: it?.brand || it?.manufacturer || '',
      brandShort: (it?.brand || '').slice(0, 10),
      discount: 0,
      name,
      subtitle: it?.description || it?.category || '',
      category: it?.category || '',
      color: (it?.color || '').toLowerCase(),
      specs: [
        it?.category && `Category: ${it.category}`,
        it?.color && `Color: ${it.color}`,
        it?.size && `Size: ${it.size}`,
      ].filter(Boolean),
      image: firstImage,
      price,
      available: true,
      source: 'internet',
      raw: it,
    };
  };

  // --- Load DB products on boot ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await backend.get(`/api/products`);
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!cancelled) setDbProducts(rows);
      } catch (err) {
        console.warn("load products error:", err?.response?.data || err.message);
      } finally {
        if (!cancelled) setIsBootLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, []);

  // --- External fetch (axios CORRECT handling) ---
  const loadNextExternal = useCallback(async () => {
    if (!extHasMore || extLoading) return;
    setExtLoading(true);
    try {
      const page = extPageRef.current;
      const search = query?.trim() || 'flashlight led light';

      let url = USE_PROXY ? `/api/ext/barcode` : `https://api.barcodelookup.com/v3/products`;

      const res = USE_PROXY
        ? await backend.get(url, { params: { search, page } })
        : await backend.get(url, { params: { search, page, key: 'YOUR_FRONTEND_KEY' } });

      const data = res?.data || {};
      const list = Array.isArray(data?.products) ? data.products : [];

      const accepted = query?.trim()
        ? list
        : list.filter(p =>
            isLightingLike(p?.category) ||
            isLightingLike(p?.product_name) ||
            isLightingLike(p?.title) ||
            isLightingLike(p?.description)
          );

      const mapped = accepted.map((p, i) => mapExtToCard(p, i));
      setExtItems(prev => [...prev, ...mapped]);

      if (list.length < 10) setExtHasMore(false);
      else extPageRef.current = page + 1;
    } catch (e) {
      console.warn('external fetch error:', e?.message || e);
      setExtHasMore(false);
    } finally {
      setExtLoading(false);
    }
  }, [extHasMore, extLoading, query]);

  // Reset ext on query change and fetch first page immediately when typing
  useEffect(() => {
    setExtItems([]);
    extPageRef.current = 1;
    setExtHasMore(true);
    if (query?.trim()) {
      (async () => { await loadNextExternal(); })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // --- Merge & subsets
  const mappedLocal = useMemo(() => (dbProducts || []).map(mapDbToCard), [dbProducts]);
  const unifiedAll = useMemo(() => [...mappedLocal, ...extItems], [mappedLocal, extItems]);

  // Source-aware dynamic bounds/options (computed from items allowed by current source checkboxes)
  const sourceFilteredForBounds = useMemo(
    () => unifiedAll.filter(p => (filters.sources || []).includes(p.source)),
    [unifiedAll, filters.sources]
  );

  const [minPrice, maxPrice] = useMemo(() => {
    if (!sourceFilteredForBounds.length) return [0, 3000];
    let min = Infinity, max = 0;
    sourceFilteredForBounds.forEach(p => {
      const v = Number(p.price || 0);
      if (Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    });
    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max) || max < 50) max = 3000;
    return [Math.max(0, Math.floor(min)), Math.min(10000, Math.ceil(max || 3000))];
  }, [sourceFilteredForBounds]);

  const dynLocal = useMemo(() => {
    const b = new Set(), c = new Set(), col = new Set();
    mappedLocal.forEach(p => {
      if (p.brand) b.add(p.brand);
      if (p.category) c.add(p.category);
      if (p.color) col.add(p.color);
    });
    return {
      brands: Array.from(b).sort(),
      categories: Array.from(c).sort(),
      colors: Array.from(col).filter(Boolean).slice(0, 12),
    };
  }, [mappedLocal]);

  const dynInternet = useMemo(() => {
    const b = new Set(), c = new Set(), col = new Set();
    extItems.forEach(p => {
      if (p.brand) b.add(p.brand);
      if (p.category) c.add(p.category);
      if (p.color) col.add(p.color);
    });
    return {
      brands: Array.from(b).sort(),
      categories: Array.from(c).sort(),
      colors: Array.from(col).filter(Boolean).slice(0, 12),
    };
  }, [extItems]);

  // Filtering + sort
  const filtered = useMemo(() => {
    const base = applyAllFilters(unifiedAll, { ...filters }, query);
    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'newest') return 0;
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'discount') return (b.discount || 0) - (a.discount || 0);
      return 0;
    });
    return sorted;
  }, [unifiedAll, filters, query, sortBy]);

  // Paging + infinite scroll kick
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => setVisibleCount(PAGE_SIZE), [query, sortBy, filters]);

  const pageItems = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  const onHitBottom = useCallback(async () => {
    const willShow = Math.min(visibleCount + PAGE_SIZE, filtered.length);
    const nearEnd = willShow > unifiedAll.length - 5;
    // Only fetch more internet pages if "internet" source is enabled
    if (nearEnd && extHasMore && (filters.sources || []).includes('internet')) {
      await loadNextExternal();
    }
    setVisibleCount((c) => Math.min(c + PAGE_SIZE, filtered.length));
  }, [visibleCount, filtered.length, unifiedAll.length, extHasMore, loadNextExternal, filters.sources]);

  const clearAll = () => { setFilters(defaultFilters); setQuery(''); setSortBy('newest'); };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className={`${styles.page} layoutPadding`}>
        <TopBar total={filtered.length} query={query} onQuery={setQuery} />

        <div className={styles.content}>
          <aside className={styles.sidebar}>
            <FiltersSidebar
              filters={filters}
              setFilters={setFilters}
              onClearAll={clearAll}

              // dynamic option feeds (split per source)
              dynamicBrandsLocal={dynLocal.brands}
              dynamicCategoriesLocal={dynLocal.categories}
              dynamicColorsLocal={dynLocal.colors}

              dynamicBrandsInternet={dynInternet.brands}
              dynamicCategoriesInternet={dynInternet.categories}
              dynamicColorsInternet={dynInternet.colors}

              // price bounds respect selected sources
              dynamicPriceMin={minPrice}
              dynamicPriceMax={maxPrice}
            />
          </aside>

          <main className={styles.gridWrap}>
            <ProductGrid
              items={pageItems}
              total={filtered.length}
              loaded={pageItems.length}
              onHitBottom={onHitBottom}
              sortBy={sortBy}
              onSortBy={setSortBy}
              isLoading={isBootLoading || (extLoading && !extItems.length)}
            />
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
